import {
  getCurrentFocusKey,
  setFocus,
} from '@noriginmedia/norigin-spatial-navigation'
import React, {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { debounce } from '../../../../shared/helpers.js'
import { useLocationHistory } from '../../../../shared/providers/LocationHistoryProvider'
import type {
  SidePanelPrivateContextValue,
  SidePanelProviderProps,
  SidePanelPublicContextValue,
} from './types'
import {
  SIDE_PANEL_ITEMS,
  defaultPrivateContextValue,
  defaultPublicContextValue,
  getItemPath,
  isAllowedItem,
  shouldKeepActiveItem,
} from './utils'

const { LIVESTREAM, MY_STATION, SEARCH, SETTINGS } = SIDE_PANEL_ITEMS

const SidePanelPublicContext = createContext<SidePanelPublicContextValue>(
  defaultPublicContextValue
)

export const SidePanelPrivateContext =
  createContext<SidePanelPrivateContextValue>(defaultPrivateContextValue)

function useSidePanelProvider() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const locationHistory = useLocationHistory()

  const [isSidePanelOpened, setIsSidePanelOpened] = useState(true)
  const [isSidePanelFocused, setIsSidePanelFocused] = useState(true)
  const [keepSidePanelOpened, setKeepSidePanelOpened] = useState(false)
  const [avoidStealFocus, setAvoidStealFocus] = useState(false)
  const [activeItem, setActiveItem] = useState('')
  const [keepLogoVisible, setKeepLogoVisible] = useState(false)

  const activeItemRef = useRef(activeItem)
  useEffect(() => {
    activeItemRef.current = activeItem
  }, [activeItem])

  useEffect(() => {
    const currentLocation = locationHistory[locationHistory.length - 1] || ''
    const itemPath = getItemPath(locationHistory)

    if (
      currentLocation === '/activate' ||
      currentLocation === '/activatecode'
    ) {
      setActiveItem('home')
      return
    }

    if (
      !shouldKeepActiveItem(activeItem, locationHistory) &&
      isAllowedItem(itemPath)
    ) {
      setActiveItem(itemPath)
    } else if (!activeItem && !isAllowedItem(itemPath)) {
      setActiveItem(SIDE_PANEL_ITEMS.HOME.to)
    }
  }, [locationHistory, activeItem])

  useEffect(() => {
    if (pathname === '/error') {
      return
    }

    const currentLocation = locationHistory[locationHistory.length - 1] || ''
    const currentRouteSegment = (currentLocation.split('/')[1] || '').split(
      '?'
    )[0]
    const isSidePanelRoute = isAllowedItem(currentRouteSegment)

    if (!isSidePanelRoute || !isAllowedItem(activeItem) || avoidStealFocus) {
      return
    }

    const currentFocusKey = getCurrentFocusKey()
    const isCurrentFocusOnSidePanel = isAllowedItem(currentFocusKey)
    const routeDerivedItem = getItemPath(locationHistory)
    const focusTarget = isAllowedItem(routeDerivedItem)
      ? routeDerivedItem
      : activeItem

    if (focusTarget !== currentFocusKey && !isCurrentFocusOnSidePanel) {
      setFocus(focusTarget)
    }
  }, [activeItem, avoidStealFocus, locationHistory])

  useEffect(() => {
    const currentLocation = locationHistory[locationHistory.length - 1] || ''
    const currentRouteSegment = (currentLocation.split('/')[1] || '').split(
      '?'
    )[0]

    if (!isAllowedItem(currentRouteSegment)) {
      setIsSidePanelFocused(false)
    }
  }, [locationHistory])

  useLayoutEffect(
    () => setIsSidePanelOpened(keepSidePanelOpened || isSidePanelFocused),
    [keepSidePanelOpened, isSidePanelFocused]
  )

  const onItemFocus = useMemo(
    () =>
      debounce((to: string) => {
        const currentActiveItem = activeItemRef.current
        if (currentActiveItem !== to) {
          navigate(`/${to}`)
        }
      }, 1000),
    [navigate]
  )

  const publicValue: SidePanelPublicContextValue = {
    hasFullWidthBackground: activeItem === MY_STATION.to,
    closeWithoutVerticalTranslate: [MY_STATION.to, SEARCH.to].includes(
      activeItem
    ),
    hasMainContentFullHeight: [LIVESTREAM.to, SEARCH.to].includes(activeItem),
    isSidePanelFocused,
    isSidePanelOpened,
    activeItem,
    isLogoHidden: !(isSidePanelOpened || keepLogoVisible),
    withoutTransitions: [LIVESTREAM.to, SETTINGS.to].includes(activeItem),
    setAvoidStealFocus,
    setKeepSidePanelOpened,
    setKeepLogoVisible,
    focusSidePanel: () => setFocus('SIDE_PANEL'),
  }

  const privateValue: SidePanelPrivateContextValue = {
    setIsSidePanelFocused,
    onItemFocus,
  }

  return { publicValue, privateValue }
}

export function useSidePanel(): SidePanelPublicContextValue {
  return useContext(SidePanelPublicContext)
}

export function SidePanelProvider({ children }: SidePanelProviderProps) {
  const { publicValue, privateValue } = useSidePanelProvider()
  return (
    <SidePanelPublicContext.Provider value={publicValue}>
      <SidePanelPrivateContext.Provider value={privateValue}>
        {children}
      </SidePanelPrivateContext.Provider>
    </SidePanelPublicContext.Provider>
  )
}
